import * as React from 'react';
import * as cx from 'classnames';
import SuperCritical, { SuperCriticalComponent } from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';
import { LloydServiceVessel } from 'data/models/LloydServiceVessel';
import { MarineTrafficServiceVessel } from 'data/models/MarineTrafficServiceVessel';
import { PSixServiceVessel } from 'data/models/PSixServiceVessel';
import { Vessel } from 'data/models/Vessel';
import { VesselServiceSettings } from 'data/models/VesselServiceSettings';
import { VesselServiceActive } from 'server/enums/VesselServiceActive';
import { VesselServiceConfigure, ISearchModel, ISearchResult, LloydSearchModel, MarineTrafficSearchModel, PSixSearchModel } from './model';
import { NullableNumericInput } from 'portcall/numericinput/view';

export const VesselServiceConfigureView = SuperCritical(({ model: m } : { model : VesselServiceConfigure }) =>
    <div className="vesselservice-configure pc-page-fixedwidth">
        {/*<loadingindicator params="request: Request" />*/}
        <div className="pc-actions-links-header">
            <a className="pc-back-link" href="" onClick={noDefault(m.GoBack)}>&lt;&lt; Back</a>
        </div>
        <h1 className="pc-page-title">
            <span className="pc-text-label-color">More</span><span className="pc-icon-bullet"></span>
            <span>Vessel Configuration</span>
        </h1>
        <PageBody model={m} />
    </div>
);

let PageBody = SuperCritical(({ model: m } : { model : VesselServiceConfigure }) =>
    <div className="page-body">
        <VesselBasicsCard model={m} />
        <DuplicateVesselsView model={m} />
        <LloydServiceSearchView model={m} />
        <MarineTrafficServiceSearchView model={m} />
        <PSixServiceSearchView model={m} />
    </div>
);

let VesselBasicsCard = SuperCritical(({ model: m } : { model : VesselServiceConfigure }) =>
    <div className="pc-card">
        <VesselBasicsHeader model={m} />
        <VesselBasicsBody model={m} />
    </div>
);

let VesselBasicsHeader = SuperCritical(({ model: m } : { model : VesselServiceConfigure }) =>
    <div className="pc-card-header">
        <div className="vessel-name">
            <span className="pc-card-title">{m.Model.Vessel() && m.Model.Vessel()!.Name()}</span>
            <div className="vessel-profile-button">
                <a href="" onClick={noDefault(m.GoToOverrides)}>Vessel Profile</a>
            </div>
        </div>
    </div>
);

let VesselBasicsBody = SuperCritical(({ model: m } : { model : VesselServiceConfigure }) =>
    <div className="vessel-details">
        <div className="left-side">
            <div className="imo">
                <div className="pc-text-smalllabel identifier">IMO</div>
                <div className="pc-text-smalltext value">{m.Model.Vessel() && m.Model.Vessel()!.IMO()}</div>
            </div>
            <div className="vin">
                <div className="pc-text-smalllabel identifier">VIN</div>
                <div className="pc-text-smalltext value">{m.Model.Vessel() && m.Model.Vessel()!.VIN()}</div>
            </div>
            <div className="mmsid">
                <div className="pc-text-smalllabel identifier">MMSID</div>
                <div className="pc-text-smalltext value">{m.Model.Vessel() && m.Model.Vessel()!.MMSID()}</div>
            </div>
            <div className="callsign">
                <div className="pc-text-smalllabel identifier">Call Sign</div>
                <div className="pc-text-smalltext value">{m.Model.Vessel() && m.Model.Vessel()!.CallSign()}</div>
            </div>
        </div>
        <div className="right-side">
            <div className="vessel-type">
                <div className="pc-text-smalllabel identifier">Vessel Type</div>
                <div className="pc-text-smalltext value">{m.Model.Vessel() && m.Model.Vessel()!.VesselType()}</div>
            </div>
            <div className="gross-tonnage">
                <div className="pc-text-smalllabel identifier">Gross Tonnage</div>
                <div className="pc-text-smalltext value">{m.Model.Vessel() && m.Model.Vessel()!.GrossTonnage.toString({ units: false })}</div>
            </div>
            <div className="length">
                <div className="pc-text-smalllabel identifier">Length Overall</div>
                <div className="pc-text-smalltext value">{m.Model.Vessel() && m.Model.Vessel()!.Length.toString()}</div>
            </div>
            <div className="beam">
                <div className="pc-text-smalllabel identifier">Beam</div>
                <div className="pc-text-smalltext value">{m.Model.Vessel() && m.Model.Vessel()!.Beam.toString()}</div>
            </div>
        </div>
    </div>
);

let DuplicateVesselsView = SuperCritical(({ model: m } : { model : VesselServiceConfigure }) =>
    m.Model.DuplicateVessels().length === 0 ? null :
        <div className="pc-card">
            <div className="pc-card-header">
                <span className="pc-card-title">Potential Duplicate Vessel</span>
            </div>
            <div className="duplicate-search-results">
                <table className="table">
                    <DuplicateVesselsTableHead />
                    <DuplicateVesselsTableBody model={m} />
                </table>
            </div>
            <div>
                {/* DC: Hide button for now; remove class when Merge is working */}
                <button className="pc-button pc-button-default hide-for-now"
                    // TH: Click action? There was none in the pre-typescript page...
                    disabled={m.SelectedDuplicateVesselId() === null}>
                    Merge
                </button>
            </div>
        </div>
);

let DuplicateVesselsTableHead = SuperCritical(() =>
    <thead>
        <tr>
            <th></th>
            <th>Vessel Name</th>
            <th>IMO</th>
            <th>MMSI</th>
            <th>Call Sign</th>
            <th>Beam</th>
            <th>Length</th>
        </tr>
    </thead>
);

let DuplicateVesselsTableBody = SuperCritical(({ model: m } : { model : VesselServiceConfigure }) =>
    <tbody>
        {m.Model.DuplicateVessels().map(v =>
            <DuplicateVesselsTableRow vessel={v} model={m} key={v.Id()} />
        )}
        {m.Model.DuplicateVessels().length > 0 &&
            <DuplicateVesselsNoneRow model={m} />
        }
    </tbody>
);

let DuplicateVesselsTableRow = SuperCritical(({ vessel: v, model: m } : { vessel : Vessel, model : VesselServiceConfigure }) =>
    <tr>
        <td>
            <input type="radio" checked={m.SelectedDuplicateVesselId() === v.Id()}
                onChange={e => e.currentTarget.checked && m.SelectedDuplicateVesselId(v.Id())} />
        </td>
        <td><span className={cx({
                highlight: !!(m.Model.Vessel() && m.Model.Vessel()!.Name() === v.Name())
            })}>
            {v.Name()}
        </span></td>
        <td><span className={cx({
                highlight: !!(m.Model.Vessel() && m.Model.Vessel()!.IMO() === v.IMO())
            })}>
            {v.IMO()}
        </span></td>
        <td><span className={cx({
                highlight: !!(m.Model.Vessel() && m.Model.Vessel()!.MMSID() === v.MMSID())
            })}>
            {v.MMSID()}
        </span></td>
        <td><span className={cx({
                highlight: !!(m.Model.Vessel() && m.Model.Vessel()!.CallSign() === v.CallSign())
            })}>
            {v.CallSign()}
        </span></td>
        <td>{v.Beam.toString()}</td>
        <td>{v.Length.toString()}</td>
    </tr>
);

let DuplicateVesselsNoneRow = SuperCritical(({ model: m } : { model : VesselServiceConfigure }) =>
    <tr>
        <td>
            <input type="radio" checked={m.SelectedDuplicateVesselId() === null}
                onChange={e => e.currentTarget.checked && m.SelectedDuplicateVesselId(null)} />
        </td>
        <td>None</td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
    </tr>
);

interface SearchViewBaseProps<TSearch> {
    model : VesselServiceConfigure;
}

abstract class SearchViewBase<TSearchModel extends ISearchModel<TServiceVessel>, TServiceVessel> extends SuperCriticalComponent<SearchViewBaseProps<TSearchModel>> {
    abstract readonly title : string;
    abstract searchModel(model : VesselServiceConfigure) : TSearchModel;
    abstract activeSetting(settings : VesselServiceSettings) : VesselServiceActive;
    abstract serviceVesselRef(vessel : Vessel) : TServiceVessel | null;
    abstract searchCriteria(search : TSearchModel) : React.ReactElement<any>;
    abstract tableHead() : React.ReactElement<any>;
    abstract searchResultRow(result : ISearchResult<TServiceVessel>, search : TSearchModel) : React.ReactElement<any>;
    abstract currentVesselRow(vessel : TServiceVessel, search : TSearchModel) : React.ReactElement<any>;
    abstract noneRow(search : TSearchModel) : React.ReactElement<any>;

    private active(m : VesselServiceConfigure) {
        return this.activeSetting(m.VesselServiceSettings) === VesselServiceActive.Active;
    }

    private currentServiceVessel(m : VesselServiceConfigure) {
        return m.Model.Vessel() && this.serviceVesselRef(m.Model.Vessel()!);
    }

    private hasResultsOrCurrentVesselRow(search : TSearchModel, m : VesselServiceConfigure) {
        return search.Results().length > 0 || !search.CurrentVesselInResults() && !!this.currentServiceVessel(m);
    }

    srender({ model: m } : SearchViewBaseProps<TSearchModel>) {
        const search = this.searchModel(m),
            currentServiceVessel = this.currentServiceVessel(m);
        return !this.active(m) ? null :
            <div className="pc-card">
                <div className="pc-card-header">
                    <span className="pc-card-title">{this.title}</span>
                </div>
                <div>
                    <div>
                        {this.searchCriteria(search)}
                        <button className="pc-button pc-button-default"
                            disabled={!search.CanSearch()}
                            onClick={search.Search}>
                            Search
                        </button>
                    </div>
                    <div className="search-results">
                        <table className="table">
                            {this.tableHead()}
                            <tbody>
                                {search.Results().map(result => this.searchResultRow(result, search))}
                                {!search.CurrentVesselInResults()
                                    && currentServiceVessel
                                    && this.currentVesselRow(currentServiceVessel, search)}
                                {this.hasResultsOrCurrentVesselRow(search, m) && this.noneRow(search)}
                            </tbody>
                        </table>
                        <div>
                            {this.hasResultsOrCurrentVesselRow(search, m) &&
                                <button className="pc-button pc-button-default"
                                        onClick={search.Link}
                                        disabled={search.SelectedIdIsCurrent()}>
                                    Link
                                </button>
                            }
                            {' '}
                            {!search.SelectedIdIsCurrent() &&
                                <a href="" onClick={noDefault(search.SelectCurrentVessel)}>Cancel</a>
                            }
                        </div>
                    </div>
                </div>
            </div>;
    }
}

class LloydServiceSearchView extends SearchViewBase<LloydSearchModel, LloydServiceVessel> {
    title = 'Lloyds Search';

    searchModel(m : VesselServiceConfigure) {
        return m.LloydSearch;
    }

    activeSetting(settings : VesselServiceSettings) {
        return settings.LloydServiceActive();
    }

    serviceVesselRef(vessel : Vessel) {
        return vessel.LloydServiceVessel();
    }

    searchCriteria(search : LloydSearchModel) {
        return (
            <LloydSearchCriteria search={search} />
        );
    }

    tableHead() {
        return (
            <LloydOrMarineTrafficTableHead />
        );
    }

    searchResultRow(result : ISearchResult<LloydServiceVessel>, search : LloydSearchModel) {
        return (
            <LloydOrMarineTrafficSearchResultRow result={result} search={search} key={result.Model.Id()} />
        );
    }

    currentVesselRow(vessel : LloydServiceVessel, search : LloydSearchModel) {
        return (
            <LloydOrMarineTrafficCurrentVesselRow vessel={vessel} search={search} />
        );
    }

    noneRow(search : LloydSearchModel) {
        return (
            <LloydOrMarineTrafficNoneRow search={search} />
        );
    }
}

class MarineTrafficServiceSearchView extends SearchViewBase<MarineTrafficSearchModel, MarineTrafficServiceVessel> {
    title = 'Marine Traffic Search';

    searchModel(m : VesselServiceConfigure) {
        return m.MarineTrafficSearch;
    }

    activeSetting(settings : VesselServiceSettings) {
        return settings.MarineTrafficServiceActive();
    }

    serviceVesselRef(vessel : Vessel) {
        return vessel.MarineTrafficServiceVessel();
    }

    searchCriteria(search : MarineTrafficSearchModel) {
        return (
            <LloydOrMarineTrafficSearchCriteria search={search} />
        );
    }

    tableHead() {
        return (
            <LloydOrMarineTrafficTableHead />
        );
    }

    searchResultRow(result : ISearchResult<MarineTrafficServiceVessel>, search : MarineTrafficSearchModel) {
        return (
            <LloydOrMarineTrafficSearchResultRow result={result} search={search} key={result.Model.Id()} />
        );
    }

    currentVesselRow(vessel : MarineTrafficServiceVessel, search : MarineTrafficSearchModel) {
        return (
            <LloydOrMarineTrafficCurrentVesselRow vessel={vessel} search={search} />
        );
    }

    noneRow(search : MarineTrafficSearchModel) {
        return (
            <LloydOrMarineTrafficNoneRow search={search} />
        );
    }
}

let LloydOrMarineTrafficSearchCriteria = SuperCritical(({ search } : { search : MarineTrafficSearchModel }) =>
    <div className="search-criteria">
        <div className="search-criterion">
            <span className="pc-text-smalllabel">IMO</span>
            {' '}
            <NullableNumericInput placeholder="IMO" value={search.IMO} />
        </div>
        <div className="search-criterion">
            <span className="pc-text-smalllabel">MMSID</span>
            {' '}
            <NullableNumericInput placeholder="MMSID" value={search.MMSID} />
        </div>
    </div>
);

let LloydSearchCriteria = SuperCritical(({ search } : { search : LloydSearchModel }) =>
    <div className="search-criteria">
        <div className="search-criterion">
            <span className="pc-text-smalllabel">IMO</span>
            {' '}
            <NullableNumericInput placeholder="IMO" value={search.IMO} />
        </div>
        <div className="search-criterion">
            <span className="pc-text-smalllabel">MMSID</span>
            {' '}
            <NullableNumericInput placeholder="MMSID" value={search.MMSID} />
        </div>
        <div className="search-criterion">
            <span className="pc-text-smalllabel">Call Sign</span>
            {' '}
            <input type="text" placeholder="Call Sign"
                value={search.CallSign()}
                onChange={e => search.CallSign(e.currentTarget.value)} />
        </div>
    </div>
);

let LloydOrMarineTrafficTableHead = SuperCritical(() =>
    <thead>
        <tr>
            <th></th>
            <th>sw</th>
            <th>Vessel Name</th>
            <th>IMO</th>
            <th>MMSI</th>
            <th>Call Sign</th>
            <th>Vessel Type</th>
            <th>Max Draft</th>
            <th>Length</th>
        </tr>
    </thead>
);

let LloydOrMarineTrafficSearchResultRow = SuperCritical(({ result, search } : { result : ISearchResult<LloydServiceVessel | MarineTrafficServiceVessel>, search : LloydSearchModel | MarineTrafficSearchModel }) =>
    <tr>
        <td>
            <input type="radio" checked={result.Model.Id() === search.SelectedResultId()}
                onChange={e => e.currentTarget.checked && search.SelectedResultId(result.Model.Id())} />
        </td>
        <td>{result.SearchResultWeight()}</td>
        <td>{result.Model.Name()}</td>
        <td>{result.Model.IMO()}</td>
        <td>{result.Model.MMSID()}</td>
        <td>{result.Model.CallSign()}</td>
        <td>{result.Model.VesselType()}</td>
        <td>{result.Model.MaxDraft.toString()}</td>
        <td>{result.Model.Length.toString()}</td>
    </tr>
);

let LloydOrMarineTrafficCurrentVesselRow = SuperCritical(({ vessel: v, search } : { vessel : LloydServiceVessel | MarineTrafficServiceVessel, search : LloydSearchModel | MarineTrafficSearchModel }) =>
    <tr>
        <td>
            <input type="radio" checked={search.SelectedIdIsCurrent()}
                onChange={e => e.currentTarget.checked && search.SelectCurrentVessel()} />
        </td>
        <td></td>
        <td>{v.Name()}</td>
        <td>{v.IMO()}</td>
        <td>{v.MMSID()}</td>
        <td>{v.CallSign()}</td>
        <td>{v.VesselType()}</td>
        <td>{v.MaxDraft.toString()}</td>
        <td>{v.Length.toString()}</td>
    </tr>
);

let LloydOrMarineTrafficNoneRow = SuperCritical(({ search } : { search : LloydSearchModel | MarineTrafficSearchModel }) =>
    <tr>
        <td>
            <input type="radio" checked={search.SelectedResultId() === null}
                onChange={e => e.currentTarget.checked && search.SelectedResultId(null)} />
        </td>
        <td></td>
        <td>None</td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
    </tr>
);

class PSixServiceSearchView extends SearchViewBase<PSixSearchModel, PSixServiceVessel> {
    title = 'PSix Search';

    searchModel(m : VesselServiceConfigure) {
        return m.PSixSearch;
    }

    activeSetting(settings : VesselServiceSettings) {
        return settings.PSixServiceActive();
    }

    serviceVesselRef(vessel : Vessel) {
        return vessel.PSixServiceVessel();
    }

    searchCriteria(search : PSixSearchModel) {
        return (
            <PSixSearchCriteria search={search} />
        );
    }

    tableHead() {
        return (
            <PSixTableHead />
        );
    }

    searchResultRow(result : ISearchResult<PSixServiceVessel>, search : PSixSearchModel) {
        return (
            <PSixSearchResultRow result={result} search={search} key={result.Model.Id()} />
        );
    }

    currentVesselRow(vessel : PSixServiceVessel, search : PSixSearchModel) {
        return (
            <PSixCurrentVesselRow vessel={vessel} search={search} />
        );
    }

    noneRow(search : PSixSearchModel) {
        return (
            <PSixNoneRow search={search} />
        );
    }
}

let PSixSearchCriteria = SuperCritical(({ search } : { search : PSixSearchModel }) =>
    <div className="search-criteria">
        <div className="search-criterion">
            <span className="pc-text-smalllabel">Vessel Name</span>
            {' '}
            <input type="text" placeholder="Vessel Name"
                value={search.VesselName()}
                onChange={e => search.VesselName(e.currentTarget.value)} />
        </div>
        <div className="search-criterion">
            <span className="pc-text-smalllabel">Call Sign</span>
            {' '}
            <input type="text" placeholder="Call Sign"
                value={search.CallSign()}
                onChange={e => search.CallSign(e.currentTarget.value)} />
        </div>
        <div className="search-criterion">
            <span className="pc-text-smalllabel">HIN</span>
            {' '}
            <input type="text" placeholder="HIN"
                value={search.HIN()}
                onChange={e => search.HIN(e.currentTarget.value)} />
        </div>
        <div className="search-criterion">
            <span className="pc-text-smalllabel">VIN</span>
            {' '}
            <input type="text" placeholder="VIN"
                value={search.VIN()}
                onChange={e => search.VIN(e.currentTarget.value)} />
        </div>
    </div>
);

let PSixTableHead = SuperCritical(() =>
    <thead>
        <tr>
            <th></th>
            <th>sw</th>
            <th>Vessel Name</th>
            <th>IMO</th>
            <th>MMSI</th>
            <th>VIN</th>
            <th>HIN</th>
            <th>Call Sign</th>
            <th>Max Draft</th>
            <th>Length</th>
        </tr>
    </thead>
);

let PSixSearchResultRow = SuperCritical(({ result, search } : { result : ISearchResult<PSixServiceVessel>, search : PSixSearchModel }) =>
    <tr>
        <td>
            <input type="radio" checked={result.Model.Id() === search.SelectedResultId()}
                onChange={e => e.currentTarget.checked && search.SelectedResultId(result.Model.Id())} />
        </td>
        <td>{result.SearchResultWeight()}</td>
        <td>{result.Model.Name()}</td>
        <td>{result.Model.IMO()}</td>
        <td>{result.Model.MMSID()}</td>
        <td>{result.Model.VIN()}</td>
        <td>{result.Model.HullIdentificationNumber()}</td>
        <td>{result.Model.CallSign()}</td>
        <td>{result.Model.MaxDraft.toString()}</td>
        <td>{result.Model.Length.toString()}</td>
    </tr>
);

let PSixCurrentVesselRow = SuperCritical(({ vessel: v, search } : { vessel : PSixServiceVessel, search : PSixSearchModel }) =>
    <tr>
        <td>
            <input type="radio" checked={search.SelectedIdIsCurrent()}
                onChange={e => e.currentTarget.checked && search.SelectCurrentVessel()} />
        </td>
        <td></td>
        <td>{v.Name()}</td>
        <td>{v.IMO()}</td>
        <td>{v.MMSID()}</td>
        <td>{v.VIN()}</td>
        <td>{v.HullIdentificationNumber()}</td>
        <td>{v.CallSign()}</td>
        <td>{v.MaxDraft.toString()}</td>
        <td>{v.Length.toString()}</td>
    </tr>
);

let PSixNoneRow = SuperCritical(({ search } : { search : PSixSearchModel }) =>
    <tr>
        <td>
            <input type="radio" checked={search.SelectedResultId() === null}
                onChange={e => e.currentTarget.checked && search.SelectedResultId(null)} />
        </td>
        <td></td>
        <td>None</td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
    </tr>
);


// WEBPACK FOOTER //
// ./apps/portcall/vesselserviceconfigure/view.tsx